<template>
  <div class="payment" id="payment" name="payment">
    <div class="payment-content" v-if="order">
      <!-- Order Summary -->
      <el-card class="order-card" shadow="hover">
        <div class="order-summary">
          <div class="order-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="#4338CA">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="order-details">
            <h2>Order #{{ order.order_num }}</h2>
            <span class="order-amount">₹{{ totalPrice }}</span>
          </div>
        </div>
      </el-card>

      <!-- Loading -->
      <el-card class="pay-card" shadow="hover" v-if="loading">
        <div class="pay-loading">
          <i class="el-icon-loading"></i>
          <span>Initializing payment...</span>
        </div>
      </el-card>

      <!-- Error -->
      <el-card class="pay-card" shadow="hover" v-if="error && !loading">
        <div class="pay-error">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#EF4444">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <p>{{ error }}</p>
          <el-button type="primary" @click="initPayment" round>Retry Payment</el-button>
        </div>
      </el-card>

      <!-- QR + Wallet Buttons -->
      <el-card class="pay-card" shadow="hover" v-if="payUrl && !loading">
        <div class="qr-section">
          <p class="section-label">Scan with any UPI App</p>
          <div class="qr-wrapper">
            <img
              :src="'https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=' + encodeURIComponent(payUrl)"
              alt="QR Code"
              class="qr-image"
            />
          </div>
          <div class="qr-status">
            <p class="qr-waiting" v-if="polling">
              <i class="el-icon-loading"></i> Waiting for payment confirmation...
            </p>
            <p class="qr-success" v-if="!polling && paid">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#4338CA">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              Payment successful! Redirecting...
            </p>
          </div>
        </div>

        <!-- Wallet Quick Pay -->
        <div class="wallet-section">
          <p class="section-label">Or pay instantly with</p>
          <div class="wallet-buttons">
            <button class="wallet-btn gpay-btn" @click="openGPay">
              <span class="wallet-icon">
                <svg viewBox="0 0 24 24" width="26" height="26">
                  <path d="M17.5 8.5c-.2 0-.4 0-.5.1l-.1-.1c-.2-.2-.6-.3-1-.3-.3 0-.5.1-.7.2l-.1-.1c-.2-.2-.6-.3-.9-.3-.4 0-.8.1-1 .3l-.2-.2c-.2-.2-.6-.3-1-.3-.3 0-.5.1-.7.2v-1.5h7.1l.1.7c0 .8-.6 1.3-1 1.3z" fill="#4285F4"/>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.7 14.5c-.6.6-1.4.9-2.2.9-.8 0-1.6-.3-2.2-.9-.6-.6-.9-1.4-.9-2.2 0-.8.3-1.6.9-2.2.6-.6 1.4-.9 2.2-.9.8 0 1.6.3 2.2.9l-1.1 1.1c-.3-.3-.7-.4-1.1-.4-.4 0-.8.2-1.1.4-.3.3-.5.7-.5 1.1s.2.8.4 1.1c.3.3.7.4 1.1.4.4 0 .8-.2 1.1-.4.3-.3.5-.7.5-1.1h-1.6v-1.5h3.2c0 .1.1.3.1.4 0 .9-.3 1.7-.9 2.3z" fill="#4285F4"/>
                  <path d="M15.4 12.8v1.5h1.2v1h-1.2v1.2h-1v-1.2h-1.2v-1h1.2v-1.5z" fill="#34A853"/>
                  <path d="M20.2 12c0-1.2-.3-2.3-.8-3.2l-2.3 1.8c.1.4.2.9.2 1.4 0 .5-.1.9-.2 1.4l2.3 1.8c.5-1 .8-2.1.8-3.2z" fill="#FBBC04"/>
                  <path d="M16.8 12c0 .4 0 .7-.1 1.1l2.3 1.8c.5-.9.8-2 .8-3.2 0-1.2-.3-2.3-.8-3.2l-2.3 1.8c.2.3.1.7.1 1.1.1.2.1.4 0 .6z" fill="#EA4335"/>
                </svg>
              </span>
              <span class="wallet-label">Google Pay</span>
            </button>
            <button class="wallet-btn phonepe-btn" @click="openPhonePe">
              <span class="wallet-icon">
                <svg viewBox="0 0 24 24" width="26" height="26">
                  <circle cx="12" cy="12" r="10" fill="#5F259F"/>
                  <text x="12" y="17" text-anchor="middle" fill="white" font-size="12" font-weight="bold">P</text>
                </svg>
              </span>
              <span class="wallet-label">PhonePe</span>
            </button>
          </div>
        </div>
      </el-card>

      <!-- Init Button -->
      <el-card class="pay-card" shadow="hover" v-if="!payUrl && !loading && !error">
        <div class="init-section">
          <p class="section-label">Pay ₹{{ totalPrice }} securely via UPI</p>
          <el-button type="primary" @click="initPayment" :loading="loading" round size="large">
            <i class="el-icon-bank-card"></i> Initiate UPI Payment
          </el-button>
        </div>
      </el-card>
    </div>

    <div class="not-found" v-else>
      <p v-if="!orderNum">No order number provided.</p>
      <div class="not-found-loading" v-else>
        <i class="el-icon-loading"></i>
        <p>Loading order...</p>
      </div>
    </div>
  </div>
</template>
<script>
import * as ordersAPI from '@/api/orders'
import * as paymentAPI from '@/api/payment'

export default {
  name: 'Payment',
  data() {
    return {
      orderNum: '',
      order: null,
      payUrl: '',
      deepPayOrderNo: '',
      deepPayMchOrderNo: '',
      polling: false,
      pollTimer: null,
      loading: false,
      error: '',
      paid: false
    }
  },
  computed: {
    totalPrice() {
      if (!this.order) return 0
      return this.order.discount_price * this.order.num
    },
    gpayUrl() {
      if (!this.payUrl) return ''
      return this.payUrl.replace('upi://pay?', 'gpay://upi/pay?')
    },
    phonePeUrl() {
      if (!this.payUrl) return ''
      return this.payUrl.replace('upi://pay?', 'phonepe://pay?')
    }
  },
  activated() {
    if (this.$route.query.orderNum !== undefined) {
      this.orderNum = this.$route.query.orderNum
    }
  },
  watch: {
    orderNum: function() {
      this.load()
    }
  },
  methods: {
    load() {
      this.error = ''
      this.payUrl = ''
      this.paid = false
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.polling = false
      }
      ordersAPI
        .showOrder(this.orderNum)
        .then(res => {
          if (res.status === 200) {
            this.order = res.data
            if (this.order.type !== 2) {
              this.initPayment()
            } else {
              this.notifySucceed('This order has already been paid')
              this.$router.push({ path: '/order' })
            }
          } else if (res.status === 20001) {
            this.loginExpired(res.msg)
          } else {
            this.notifyError('Failed to load order', res.msg)
          }
        })
        .catch(err => {
          this.notifyError('Failed to load order', err)
        })
    },
    initPayment() {
      this.loading = true
      this.error = ''
      paymentAPI.initOnlinePayment({
        order_num: String(this.order.order_num),
        amount: String(this.totalPrice),
        nickname: this.$store.getters.getUser
          ? this.$store.getters.getUser.nickname || 'Customer'
          : 'Customer',
        phone: this.order.address_phone || '',
        email: this.order.address_email || ''
      }).then(res => {
        this.loading = false
        if (res.status === 200 && res.data && res.data.pay_url) {
          this.payUrl = res.data.pay_url
          this.deepPayOrderNo = res.data.order_no
          this.deepPayMchOrderNo = res.data.mch_order_no
          this.polling = true
          this.startPolling()
        } else {
          this.error = res.msg || 'Failed to initialize payment'
        }
      }).catch(err => {
        this.loading = false
        this.error = 'Network error. Please try again.'
      })
    },
    openGPay() {
      if (this.gpayUrl) { window.location.href = this.gpayUrl }
    },
    openPhonePe() {
      if (this.phonePeUrl) { window.location.href = this.phonePeUrl }
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        ordersAPI.showOrder(this.order.order_num).then(res => {
          if (res.data && res.data.type === 2) {
            clearInterval(this.pollTimer)
            this.polling = false
            this.paid = true
            this.notifySucceed('Payment successful!')
            setTimeout(() => this.$router.push({ path: '/order' }), 1500)
          }
        }).catch(() => {})
      }, 5000)
    }
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  }
}
</script>

<style scoped>
.payment {
  background: linear-gradient(135deg, #EEF2FF 0%, #EFF6FF 50%, #EEF2FF 100%);
  min-height: 100vh;
  padding: 40px 20px;
}

.order-card {
  width: 520px;
  margin: 0 auto 20px;
  border-radius: 16px;
  border: none;
}
.order-card >>> .el-card__body { padding: 24px 32px; }
.order-summary { display: flex; align-items: center; gap: 16px; }
.order-details h2 { margin: 0 0 4px; font-size: 16px; color: #374151; font-weight: 600; }
.order-amount { font-size: 28px; font-weight: 700; color: #4338CA; }

.pay-card {
  width: 520px;
  margin: 0 auto 20px;
  border-radius: 16px;
  border: none;
}
.pay-card >>> .el-card__body { padding: 32px; }
.section-label { text-align: center; color: #6B7280; font-size: 14px; margin-bottom: 20px; }

.init-section { text-align: center; }
.init-section .el-button--large { padding: 14px 48px; font-size: 16px; border-radius: 12px; }

.qr-section { text-align: center; }
.qr-wrapper {
  display: inline-block;
  background: #fff;
  border: 3px solid #4338CA;
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 4px 24px rgba(67, 56, 202, 0.12);
}
.qr-image { display: block; width: 240px; height: 240px; }
.qr-status { margin-top: 16px; min-height: 24px; }
.qr-waiting { color: #F59E0B; font-weight: 600; font-size: 14px; }
.qr-waiting i { margin-right: 6px; }
.qr-success {
  color: #4338CA; font-weight: 700; font-size: 15px;
  display: flex; align-items: center; justify-content: center; gap: 6px;
}

.wallet-section { margin-top: 28px; padding-top: 24px; border-top: 1px solid #E5E7EB; }
.wallet-buttons { display: flex; gap: 16px; justify-content: center; }
.wallet-btn {
  flex: 1; max-width: 200px;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  padding: 14px 20px; border: 2px solid transparent; border-radius: 14px;
  font-size: 15px; font-weight: 600; cursor: pointer;
  transition: all 0.25s ease; outline: none;
}
.wallet-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
.wallet-btn:active { transform: translateY(0); }
.wallet-icon { display: flex; align-items: center; }
.wallet-label { white-space: nowrap; }

.gpay-btn { background: #fff; border-color: #D1D5DB; color: #374151; }
.gpay-btn:hover { background: #F8FAFC; border-color: #4285F4; box-shadow: 0 6px 20px rgba(66,133,244,0.2); }

.phonepe-btn { background: #fff; border-color: #D1D5DB; color: #374151; }
.phonepe-btn:hover { background: #FAF5FF; border-color: #5F259F; box-shadow: 0 6px 20px rgba(95,37,159,0.2); }

.pay-loading { text-align: center; padding: 40px; color: #4338CA; font-size: 16px; }
.pay-loading i { font-size: 28px; margin-right: 10px; }

.pay-error { text-align: center; padding: 20px; }
.pay-error p { color: #EF4444; margin: 16px 0 20px; font-size: 14px; }

.not-found { text-align: center; padding: 120px 20px; color: #9CA3AF; font-size: 16px; }
.not-found-loading i { font-size: 32px; color: #4338CA; display: block; margin-bottom: 12px; }
</style>
